import { useEffect, useState } from 'react'
import { licenseApiService, type IssuanceAccess } from '@/services/licenseApiService'
import { useAuth } from '@/hooks/useAuth'

// Whether this installation may issue licenses at all.
//
// Only the server can answer: a customer install has no signing key and replies
// 501, and an account that is not one of the vendor's replies 403. Deliberately
// not a role check and not a build flag - every client admin is created a
// platform owner, so a role check showed the form to every customer, and a build
// flag can be talked into showing a form the server will refuse.
//
// The answer is fetched once per account and shared, because the sidebar and
// the issuing page both need it and two probes would show one of them a menu
// entry the other has already hidden.
//
// It is held against the account that earned it. Signing in is a route change,
// not a page load, so the module outlives the session: cached on its own, the
// vendor's "yes" was still here when jshine_admin signed in next and it drew
// them the menu entry the server then refused (seen on UAT 2026-09-25).
let cachedFor: string | null = null
let cached: IssuanceAccess | null = null
let pending: Promise<IssuanceAccess> | null = null
let pendingFor: string | null = null

function probe(account: string): Promise<IssuanceAccess> {
  if (cachedFor === account && cached !== null) return Promise.resolve(cached)
  // A probe belonging to whoever was signed in before answers nothing about
  // this account, so drop it rather than adopt its result.
  if (pendingFor !== account) {
    pending = null
    pendingFor = account
  }
  if (!pending) {
    pending = licenseApiService
      .issuanceAccess()
      // A network failure is not an answer. Treat it as "cannot issue" for now
      // and let the next mount ask again, rather than caching a guess.
      .catch((): IssuanceAccess => 'refused')
      .then((access) => {
        // Only a yes is kept. "Set up two-factor first" changes the moment
        // they do, and the page must notice without a reload.
        if (access === 'allowed') {
          cached = access
          cachedFor = account
        }
        pending = null
        return access
      })
  }
  return pending
}

/**
 * `canIssue` is `null` while the server has not answered yet. Callers draw
 * nothing in that state: showing the entry and taking it away reads as a bug.
 *
 * A vendor account with no second factor gets `canIssue: true` and
 * `needsTOTP: true`: the entry stays, and the page says what is missing.
 */
export function useLicenseIssuer(): { canIssue: boolean | null; needsTOTP: boolean } {
  const { user } = useAuth()
  const account = user?.account ?? ''
  const [access, setAccess] = useState<IssuanceAccess | null>(
    cachedFor === account && account !== '' ? cached : null,
  )

  useEffect(() => {
    // Signed out: nobody may issue, and nothing is worth asking the server.
    if (!account) {
      setAccess('refused')
      return
    }
    if (cachedFor === account && cached !== null) {
      setAccess(cached)
      return
    }
    setAccess(null)
    let cancelled = false
    void probe(account).then((answer) => {
      if (!cancelled) setAccess(answer)
    })
    return () => {
      cancelled = true
    }
  }, [account, user?.totp_enabled])

  return {
    canIssue: access === null ? null : access !== 'refused',
    needsTOTP: access === 'needs-totp',
  }
}
